import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Minus, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { api } from "@/api/client";
import type { DashboardBrandSeries, DashboardData, MetricBucket } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import { TrendChart, SERIES_COLORS } from "./TrendChart";
import { SentimentExplanationPanel, type SentimentExplanationCompetitorOption } from "./SentimentExplanationPanel";

const METRIC_OPTIONS = [
  { value: "visibility", label: "Visibility" },
  { value: "sentiment", label: "Sentiment" },
  { value: "overall_score", label: "Score" },
];

const RANGE_OPTIONS: { days: number; bucket: MetricBucket; label: string; rangeKey: string }[] = [
  { days: 7, bucket: "day", label: "7D", rangeKey: "7d" },
  { days: 30, bucket: "day", label: "30D", rangeKey: "30d" },
  { days: 90, bucket: "week", label: "90D", rangeKey: "90d" },
  { days: 365, bucket: "month", label: "1Y", rangeKey: "365d" },
];

/**
 * Converts a dashboard brand row into a competitor selector option.
 *
 * @param brand Dashboard brand row.
 * @return Competitor selector option.
 */
function toCompetitorOption(brand: DashboardBrandSeries): SentimentExplanationCompetitorOption {
  return { brandId: brand.brand_id, brandName: brand.brand_name };
}

/**
 * Renders a trend delta chip for the dashboard hero score.
 *
 * @param props Delta chip props.
 * @return Delta chip markup.
 */
function DeltaChip({ label, value }: { label: string; value: number | null }) {
  const direction = value === null ? "none" : value > 0.05 ? "up" : value < -0.05 ? "down" : "flat";
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">{label}</span>
      <span
        className={cn(
          "inline-flex items-center gap-1 text-[14px] font-semibold tabular-nums",
          direction === "up" && "text-green-600",
          direction === "down" && "text-red-500",
          (direction === "flat" || direction === "none") && "text-slate-400",
        )}
      >
        <Icon className="w-3.5 h-3.5" />
        {value === null ? "--" : `${value > 0 ? "+" : ""}${value.toFixed(1)}`}
      </span>
    </div>
  );
}

/**
 * Renders the metrics dashboard view.
 *
 * @return Dashboard view markup.
 */
export function DashboardView() {
  const [metric, setMetric] = useState("visibility");
  const [rangeIdx, setRangeIdx] = useState(1); // 30D default
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = RANGE_OPTIONS[rangeIdx]!;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.metrics.dashboard({ metric, bucket: range.bucket, days: range.days })
      .then(d => { if (!cancelled) { setData(d); setError(""); } })
      .catch(err => { if (!cancelled) setError(err?.message || "Failed to load dashboard"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [metric, rangeIdx]);

  if (loading && !data) return <Spinner message="Loading dashboard..." size="lg" />;
  if (error && !data) return <ErrorState message={error} onRetry={() => setRangeIdx(i => i)} />;
  if (!data) return null;

  if (!data.has_data) {
    return (
      <EmptyState
        icon={CalendarClock}
        title="No tracked metrics yet"
        description="Schedule a study to start building visibility and sentiment trends, or run a comparison — every completed run feeds this dashboard."
        action={
          <Button asChild>
            <Link to="/app/studies">Set up a scheduled study</Link>
          </Button>
        }
      />
    );
  }

  const metricLabel = METRIC_OPTIONS.find(m => m.value === metric)?.label ?? metric;
  // Hero brand: the workspace's primary brand when it has data, else the brand
  // with the richest history in the window.
  const hero =
    data.brands.find(b => b.is_primary && b.series.length > 0)
    ?? [...data.brands].sort((a, b) => b.series.length - a.series.length)[0]
    ?? null;
  const competitorOptions = hero
    ? data.brands.filter(brand => brand.brand_id !== hero.brand_id).map(toCompetitorOption)
    : [];

  const sov = data.share_of_voice.slice(0, 8);
  const sovOverflow = data.share_of_voice.length - sov.length;
  const maxShare = Math.max(...sov.map(s => s.share), 1);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
          {METRIC_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setMetric(option.value)}
              className={cn(
                "px-3 h-7 rounded text-[12px] font-medium transition-colors cursor-pointer",
                metric === option.value ? "bg-brand-600 text-white" : "text-slate-500 hover:text-brand-950",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
          {RANGE_OPTIONS.map((option, i) => (
            <button
              key={option.label}
              onClick={() => setRangeIdx(i)}
              className={cn(
                "px-3 h-7 rounded text-[12px] font-medium transition-colors cursor-pointer tabular-nums",
                rangeIdx === i ? "bg-brand-950 text-white" : "text-slate-500 hover:text-brand-950",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero: primary brand pulse */}
      {hero && (
        <Card className="animate-fade-up overflow-hidden">
          <CardContent className="py-6 px-6">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-400">
                    {hero.brand_name}
                  </span>
                  {hero.is_primary && <Badge variant="winner">Primary</Badge>}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[44px] leading-none font-bold text-brand-950 tabular-nums">
                    {hero.latest !== null ? hero.latest.toFixed(1) : "--"}
                  </span>
                  <span className="text-[13px] text-slate-400">avg {metricLabel.toLowerCase()}</span>
                </div>
              </div>
              <div className="flex gap-8">
                <DeltaChip label="vs yesterday" value={hero.deltas.day} />
                <DeltaChip label="vs last week" value={hero.deltas.week} />
                <DeltaChip label="vs last month" value={hero.deltas.month} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {metric === "sentiment" && hero && (
        <SentimentExplanationPanel
          brandId={hero.brand_id}
          brandName={hero.brand_name}
          rangeKey={range.rangeKey}
          competitorOptions={competitorOptions}
        />
      )}

      {/* Trend chart */}
      <Card className="animate-fade-up" style={{ animationDelay: "60ms" }}>
        <CardHeader>
          <CardTitle>{metricLabel} Over Time</CardTitle>
          <CardDescription>
            Average {metricLabel.toLowerCase()} per {range.bucket} across all completed runs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TrendChart
            brands={data.brands}
            bucket={range.bucket}
            heroBrandId={hero?.brand_id ?? null}
            title={`${metricLabel} over time`}
            caption={`Average ${metricLabel.toLowerCase()} per ${range.bucket} · last ${range.label}`}
            exportTopic="trends"
            exportChart={metric}
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Share of voice */}
        <Card className="animate-fade-up" style={{ animationDelay: "120ms" }}>
          <CardHeader>
            <CardTitle>Share of Voice</CardTitle>
            <CardDescription>Visibility-weighted presence in AI answers this period</CardDescription>
          </CardHeader>
          <CardContent>
            {sov.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-slate-400">
                No visibility data this period. Blind query studies feed this panel.
              </p>
            ) : (
              <div className="space-y-3">
                {sov.map((entry, i) => (
                  <div key={entry.brand_name}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[11px] font-semibold text-slate-300 tabular-nums w-4">{i + 1}</span>
                        <span className={cn(
                          "text-[13px] font-medium truncate",
                          entry.tracked ? "text-brand-950" : "text-slate-500",
                        )}>
                          {entry.brand_name}
                        </span>
                        {!entry.tracked && <Badge variant="muted">untracked</Badge>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {entry.avg_sentiment !== null && (
                          <span className="text-[11px] text-slate-400 tabular-nums">
                            sent {entry.avg_sentiment.toFixed(0)}
                          </span>
                        )}
                        <span className="text-[12px] font-semibold text-brand-950 tabular-nums w-12 text-right">
                          {entry.share.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${(entry.share / maxShare) * 100}%`,
                          backgroundColor: entry.tracked
                            ? SERIES_COLORS[data.brands.findIndex(b => b.brand_id === entry.brand_id) % SERIES_COLORS.length] ?? SERIES_COLORS[0]
                            : "#cbd5e1",
                        }}
                      />
                    </div>
                  </div>
                ))}
                {sovOverflow > 0 && (
                  <p className="text-[11px] text-slate-400 pt-1">+{sovOverflow} more brand{sovOverflow === 1 ? "" : "s"} mentioned</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New brands appearing in answers */}
        <Card className="animate-fade-up" style={{ animationDelay: "180ms" }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent-500" />
              New Names in Answers
            </CardTitle>
            <CardDescription>Untracked brands that first appeared this period</CardDescription>
          </CardHeader>
          <CardContent>
            {data.new_brands.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-slate-400">
                No new brands surfaced in this period.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.new_brands.slice(0, 8).map(entry => (
                  <div key={entry.brand_name} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                    <div>
                      <p className="text-[13px] font-medium text-brand-950">{entry.brand_name}</p>
                      <p className="text-[11px] text-slate-400">
                        First seen {new Date(entry.first_seen.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {" "}&middot; {entry.appearances} answer{entry.appearances === 1 ? "" : "s"}
                      </p>
                    </div>
                    {entry.avg_sentiment !== null && (
                      <Badge variant={entry.avg_sentiment >= 60 ? "success" : entry.avg_sentiment >= 40 ? "muted" : "error"}>
                        sent {entry.avg_sentiment.toFixed(0)}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
